import { useState, type FormEvent, type ReactNode } from 'react';

type Mode = 'partnership' | 'resident';
type Status = 'idle' | 'submitting' | 'success' | 'error';

const FORM_URL = 'https://readdy.ai/api/form/db4rip0op8h4ruqk2630';
const MAX_MSG = 500;

const inputCls = 'mt-2 block w-full min-h-[48px] rounded-[8px] border bg-background-50 px-4 text-[16px] text-foreground-950 placeholder:text-foreground-500 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-primary-200';

interface FieldProps {
  id: string;
  label: string;
  error?: string;
  optional?: boolean;
  children: ReactNode;
}

function Field({ id, label, error, optional, children }: FieldProps) {
  return (
    <div>
      <label htmlFor={id} className="font-label text-[15px] font-semibold text-foreground-950">
        {label}
        {optional && <span className="ml-1 font-normal text-foreground-600">(optional)</span>}
      </label>
      {children}
      {error && <p id={`${id}-err`} className="mt-1.5 text-[14px] font-medium text-accent-700">{error}</p>}
    </div>
  );
}

interface InquiryFormProps {
  initialMode: Mode;
}

export default function InquiryForm({ initialMode }: InquiryFormProps) {
  const [mode, setMode] = useState<Mode>(initialMode);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');
  const [msgLen, setMsgLen] = useState(0);

  const border = (k: string) => (errors[k] ? 'border-accent-500' : 'border-background-300');

  const validate = (data: FormData) => {
    const e: Record<string, string> = {};
    const get = (k: string) => String(data.get(k) || '').trim();
    if (!get('full_name')) e.full_name = 'Please enter your full name.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(get('email'))) e.email = 'Please enter a valid email address.';
    if (!get('community_name')) e.community_name = mode === 'partnership' ? 'Please enter the community or organization name.' : 'Please enter your community name.';
    if (mode === 'partnership' && !get('city_state')) e.city_state = 'Please enter the city and state.';
    if (mode === 'resident' && !get('inquiry_category')) e.inquiry_category = 'Please choose a category.';
    const msg = get('message');
    if (!msg) e.message = 'Please add a short message.';
    else if (msg.length > MAX_MSG) e.message = `Please keep your message under ${MAX_MSG} characters.`;
    return e;
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    setFormError('');

    const trap = String(data.get('website_alt') || '').trim();
    if (trap) { setStatus('success'); return; }
    data.delete('website_alt');

    const found = validate(data);
    setErrors(found);
    if (Object.keys(found).length) { setStatus('idle'); return; }

    data.set('inquiry_type', mode === 'partnership' ? 'Property Partnership' : 'Resident Support');
    const body = new URLSearchParams();
    data.forEach((v, k) => { if (typeof v === 'string' && v.trim() !== '') body.append(k, v); });

    setStatus('submitting');
    try {
      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: unknown } } | null = null;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const detail = typeof parsed?.meta?.detail === 'string' ? parsed.meta.detail : '';
      const serverMsg = parsed?.meta?.message || parsed?.message || detail || responseText;
      const isSpam = /spam/i.test(serverMsg || '');
      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        form.reset();
        setMsgLen(0);
      } else {
        setStatus('error');
        setFormError(serverMsg || 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setFormError('We could not reach the server. Please check your connection and try again.');
    }
  };

  if (status === 'success') {
    return (
      <div className="rounded-[12px] border border-background-200 p-8 md:p-10" role="status">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-50 text-[24px] text-accent-500">
          <i className="ri-check-line" aria-hidden="true"></i>
        </span>
        <h3 className="mt-6 font-heading text-[24px] font-semibold text-foreground-950">Your message has been submitted.</h3>
        <button type="button" onClick={() => setStatus('idle')} className="mt-6 min-h-[48px] cursor-pointer whitespace-nowrap rounded-[9px] border border-background-300 px-6 font-label text-[15px] font-semibold text-foreground-950 hover:border-foreground-950">
          Send another inquiry
        </button>
      </div>
    );
  }

  return (
    <form id="contact-inquiry" data-readdy-form onSubmit={onSubmit} noValidate className="relative scroll-mt-28">
      <div className="relative inline-grid grid-cols-2 rounded-full border border-background-300 px-1 py-1" role="group" aria-label="Inquiry type">
        <span
          aria-hidden="true"
          className="pointer-events-none absolute bottom-1 left-1 top-1 w-[calc(50%-4px)] rounded-full bg-primary-500 transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform motion-reduce:transition-none"
          style={{ transform: mode === 'resident' ? 'translateX(100%)' : 'translateX(0)' }}
        />
        {([['partnership', 'Property Partnership'], ['resident', 'Resident Support']] as const).map(([key, label]) => (
          <button
            key={key}
            type="button"
            aria-pressed={mode === key}
            onClick={() => { setMode(key); setErrors({}); }}
            className={`relative z-10 min-h-[44px] cursor-pointer whitespace-nowrap rounded-full px-4 font-label text-[14px] font-semibold transition-colors duration-200 sm:px-5 sm:text-[15px] ${mode === key ? 'text-foreground-950' : 'text-foreground-700 hover:text-foreground-950'}`}
          >
            {label}
          </button>
        ))}
      </div>
      <p className="mt-4 text-[15px] text-foreground-700">
        {mode === 'partnership'
          ? 'For boards, owners and developers interested in management services.'
          : 'For residents with a question or request about their community. Please do not include unit access codes or other private details.'}
      </p>

      <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
        <Field id="full_name" label="Full name" error={errors.full_name}>
          <input id="full_name" name="full_name" type="text" autoComplete="name" className={`${inputCls} ${border('full_name')}`} aria-invalid={!!errors.full_name} aria-describedby={errors.full_name ? 'full_name-err' : undefined} />
        </Field>
        <Field id="email" label="Email" error={errors.email}>
          <input id="email" name="email" type="email" autoComplete="email" className={`${inputCls} ${border('email')}`} aria-invalid={!!errors.email} aria-describedby={errors.email ? 'email-err' : undefined} />
        </Field>
        <Field id="community_name" label={mode === 'partnership' ? 'Community or organization name' : 'Community name'} error={errors.community_name}>
          <input id="community_name" name="community_name" type="text" className={`${inputCls} ${border('community_name')}`} aria-invalid={!!errors.community_name} aria-describedby={errors.community_name ? 'community_name-err' : undefined} />
        </Field>
        {mode === 'partnership' ? (
          <>
            <Field id="city_state" label="City / state" error={errors.city_state}>
              <input id="city_state" name="city_state" type="text" className={`${inputCls} ${border('city_state')}`} aria-invalid={!!errors.city_state} aria-describedby={errors.city_state ? 'city_state-err' : undefined} />
            </Field>
            <Field id="unit_count" label="Approximate number of units" optional>
              <input id="unit_count" name="unit_count" type="number" min={1} inputMode="numeric" className={`${inputCls} border-background-300`} />
            </Field>
          </>
        ) : (
          <Field id="inquiry_category" label="Inquiry category" error={errors.inquiry_category}>
            <div className="relative">
              <select id="inquiry_category" name="inquiry_category" defaultValue="" className={`${inputCls} ${border('inquiry_category')} cursor-pointer appearance-none pr-10`} aria-invalid={!!errors.inquiry_category} aria-describedby={errors.inquiry_category ? 'inquiry_category-err' : undefined}>
                <option value="" disabled>Select a category</option>
                <option>Maintenance request</option>
                <option>Common area concern</option>
                <option>Billing or account question</option>
                <option>Community notice or event</option>
                <option>General question</option>
              </select>
              <span className="pointer-events-none absolute right-3 top-1/2 flex h-5 w-5 -translate-y-1/4 items-center justify-center text-foreground-600"><i className="ri-arrow-down-s-line" aria-hidden="true"></i></span>
            </div>
          </Field>
        )}
        <div className="sm:col-span-2">
          <Field id="message" label="Message" error={errors.message}>
            <textarea
              id="message"
              name="message"
              rows={5}
              maxLength={MAX_MSG}
              onChange={(e) => setMsgLen(e.target.value.length)}
              className={`${inputCls} ${border('message')} py-3`}
              aria-invalid={!!errors.message}
              aria-describedby={errors.message ? 'message-err' : undefined}
            />
          </Field>
          <p className="mt-1.5 text-right text-[13px] text-foreground-600">{msgLen}/{MAX_MSG}</p>
        </div>
      </div>

      <div className="fld-alt-wrap">
        <label htmlFor="website_alt">Website</label>
        <input id="website_alt" name="website_alt" type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly onFocus={(e) => e.currentTarget.removeAttribute('readonly')} />
      </div>

      {status === 'error' && formError && (
        <p className="mt-4 rounded-[8px] border border-accent-300 bg-accent-50 px-4 py-3 text-[15px] text-accent-900" role="alert">{formError}</p>
      )}

      <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-end">
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="min-h-[48px] cursor-pointer whitespace-nowrap rounded-[9px] bg-primary-500 px-7 font-label text-[15px] font-semibold text-foreground-950 transition-colors duration-200 hover:bg-primary-400 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {status === 'submitting' ? 'Sending...' : mode === 'partnership' ? 'Request a Proposal' : 'Send Inquiry'}
        </button>
      </div>
    </form>
  );
}